<script setup>
import { reactive, onMounted, ref, computed, watch, nextTick } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import ComexPipelineStatus from '@/components/base/fluxo/ComexPipelineStatus.vue'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import ModalDeBusca from '@/components/base/modal/ModalDeBusca.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import SemAcessoModulo from '@/components/base/util/SemAcessoModulo.vue'
import { filialSearch } from '@/search-definitions/filialSearch'
import { fornecedorSearch } from '@/search-definitions/fornecedorSearch'
import { templatePrecificacaoSearch } from '@/search-definitions/templatePrecificacaoSearch'
import { criarConfigBuscaProduto } from '@/search-definitions/produtoSearch'
import { ST } from '@/constants/ST'
import { useNavegacao } from '@/composables/useNavegacao'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idContratoCompra: 0,
      idFilial: null,
      nomeFilial: '',
      idParceiroNegocioFornecedor: null,
      nomeFornecedor: '',
      numeroContratoFornecedor: '',
      codigoContratoCompra: '',
      dataContrato: '',
      lstMoeda: '',
      percentualTolerancia: null,
      lstStatusContrato: '',
      valorTotalContrato: null,
      atributosPrecificacaoJson: null
    })
  },
  // GUARDIÃO: Declarando as propriedades injetadas pelo ComexDrilldownLayout para evitar avisos "Extraneous non-props attributes"
  drilldownMode: {
    type: Boolean,
    default: false
  },
  pipelineProps: {
    type: Object,
    default: () => ({})
  }
})

// GUARDIÃO: Declarando explicitamente os eventos do Pipeline para evitar avisos "Extraneous non-emits event listeners"
const emit = defineEmits([
  'itemAtualizado',
  'cancelar',
  'show-notification',
  'drilldown-push',
  'drilldown-pop',
  'pipeline-navegar'
])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const mostrarAuditoria = ref(false)
const btnTemplate = ref(null)

const listas = reactive({})

const accordionState = reactive({
  dadosBasicos: true,
  pricing: !props.itemParaEditar || !props.itemParaEditar.idContratoCompra,
  produtos: false,
  condicoesPagamento: false,
  purchaseOrders: !!(props.itemParaEditar && props.itemParaEditar.idContratoCompra)
})

const produtosContrato = ref([])
const isLoadingProdutos = ref(false)
const produtoEmEdicao = ref(null)

const condicoesPagamentoContrato = ref([])
const isLoadingCondicoes = ref(false)
const condicaoEmEdicao = ref(null)

const purchaseOrdersContrato = ref([])
const isLoadingPurchaseOrders = ref(false)

const purchaseOrdersContratoOrdenadas = computed(() => {
  return [...purchaseOrdersContrato.value].sort((a, b) => {
    if (!a.dataEmissao) return 1
    if (!b.dataEmissao) return -1
    return new Date(a.dataEmissao) - new Date(b.dataEmissao)
  })
})

const percentualPendentePurchaseOrders = computed(() => {
  const totalContrato = Number(item.value.valorTotalContrato) || 0
  if (totalContrato <= 0) return 0
  const totalAlocado = purchaseOrdersContrato.value.reduce((acc, c) => acc + (Number(c.valorTotalNegociado) || 0), 0)
  const percentualAlocado = Math.min(100, Math.round((totalAlocado / totalContrato) * 100))
  return 100 - percentualAlocado
})

const showModalProduto = computed({
  get: () => !!produtoEmEdicao.value,
  set: (val) => { if (!val) produtoEmEdicao.value = null }
})

const showModalCondicao = computed({
  get: () => !!condicaoEmEdicao.value,
  set: (val) => { if (!val) condicaoEmEdicao.value = null }
})

const modalExclusaoProduto = ref({
  id: null
})
const modalExclusaoProdutoRef = ref(null)

const modalExclusaoCondicao = ref({
  id: null
})
const modalExclusaoCondicaoRef = ref(null)

const modalConfirmacaoTrocaRef = ref(null)

const {
  item,
  isLoading,
  isEditing,
  errors, // GUARDIÃO: Extraído para podermos pintar as bordas vermelhas antecipadamente
  focarPrimeiroErro, // GUARDIÃO: Extraído para dar foco no primeiro erro visualmente
  submitForm,
  initializeForm,
  handleCancel
} = useFormulario(props, emit)

const entityInfoContrato = computed(() => ({
  id: item.value.idContratoCompra,
  name: item.value.codigoContratoCompra || item.value.numeroContratoFornecedor,
  nameLabel: 'Código'
}))
const podeEditar = computed(() => {
  if (authStore.isAdminInterno) return true
  if (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO')) return true
  return authStore.getPermsDoModulo('IMPORTACAO').some(p => p === 'COMPLETO' || (p.includes('CONTRATOS') && p.includes('COMPLETO')))
})

const temModuloImportacao = computed(() => {
  return authStore.temModulo('IMPORTACAO')
})

const temContratosCompraCompleto = computed(() => {
  if (authStore.isAdminInterno) return true
  if (authStore.pode('CONTRATOS_COMPRA_COMPLETO')) return true
  return authStore.getPermsDoModulo('IMPORTACAO').some(p =>
    p === 'COMPLETO' || (p.includes('CONTRATOS') && p.includes('COMPLETO'))
  )
})

const temCotasCompleto = computed(() => {
  if (authStore.isAdminInterno) return true
  if (authStore.pode('COTAS_COMPLETO')) return true
  return authStore.getPermsDoModulo('IMPORTACAO').some(p =>
    p === 'COMPLETO' || (p.includes('COTAS') && p.includes('COMPLETO'))
  )
})

const temVisualizacao = computed(() => {
  if (authStore.isAdminInterno) return true
  if (authStore.pode('CONTRATOS_COMPRA_VISUALIZACAO')) return true
  if (authStore.pode('COTAS_VISUALIZACAO')) return true
  return authStore.getPermsDoModulo('IMPORTACAO').some(p =>
    p === 'COMPLETO' || p.includes('VISUALIZACAO')
  )
})

const carregarProdutos = async () => {
  if (!item.value.idContratoCompra) {
    produtosContrato.value = []
    return
  }
  isLoadingProdutos.value = true
  try {
    const res = await api.get('/ContratoCompraProdutos', { params: { idContratoCompra: item.value.idContratoCompra } })
    produtosContrato.value = res.data
  } catch (e) {
    emit('show-notification', { message: 'Erro ao carregar produtos.', type: 'error' })
  } finally {
    isLoadingProdutos.value = false
  }
}

const carregarCondicoesPagamento = async () => {
  if (!item.value.idContratoCompra) {
    condicoesPagamentoContrato.value = []
    return
  }
  isLoadingCondicoes.value = true
  try {
    const res = await api.get('/ContratoCompraCondicoesPg', { params: { idContratoCompra: item.value.idContratoCompra } })
    condicoesPagamentoContrato.value = res.data
  } catch (e) {
    emit('show-notification', { message: 'Erro ao carregar condições de pagamento.', type: 'error' })
  } finally {
    isLoadingCondicoes.value = false
  }
}

const carregarPurchaseOrders = async () => {
  if (!item.value.idContratoCompra) {
    purchaseOrdersContrato.value = []
    return
  }
  isLoadingPurchaseOrders.value = true
  try {
    const res = await api.get('/PurchaseOrders', {
      params: { idContratoCompra: item.value.idContratoCompra }
    })
    purchaseOrdersContrato.value = res.data
  } catch (e) {
    emit('show-notification', { message: 'Erro ao carregar purchase orders.', type: 'error' })
  } finally {
    isLoadingPurchaseOrders.value = false
  }
}

const abrirPurchaseOrder = (po = null, funcaoAdicional = null) => {
  // Quando é uma ADIÇÃO vinda do contrato, abre o PurchaseOrderForm em modo de espera
  // (prePreencher = null) para que o usuário decida se quer pré-preenchimento.
  // O PurchaseOrderForm agora busca os dados do contrato diretamente do banco
  // (GET /ContratosCompra/{id}) quando o usuário opta por pré-preenchimento.
  // O contextoPai carrega apenas o idContratoCompra (obrigatório) e
  // numeroContratoFornecedor (informativo para o header do grid).
  if (!po && funcaoAdicional === 'adicao' && item.value.idContratoCompra) {
    emit('drilldown-push', {
      type: 'purchaseOrder',
      id: null,
      props: {
        itemParaEditar: null,
        funcao: 'adicao',
        prePreencher: null,
        modoContratoCompra: true,
        contextoPai: {
          idContratoCompra: item.value.idContratoCompra,
          numeroContratoFornecedor: item.value.numeroContratoFornecedor
        }
      }
    })
    return
  }

  const propsBase = {
    itemParaEditar: po,
    modoContratoCompra: true,
    contextoPai: {
      idContratoCompra: item.value.idContratoCompra,
      numeroContratoFornecedor: item.value.numeroContratoFornecedor
    }
  }
  if (funcaoAdicional) {
    propsBase.funcao = funcaoAdicional
  }
  emit('drilldown-push', {
    type: 'purchaseOrder',
    id: po?.idPurchaseOrder,
    props: propsBase
  })
}

const handlePipelineClick = (etapa) => {
  if (etapa.key === 'purchaseOrder') {
    abrirPurchaseOrder()
  } else {
    emit('pipeline-navegar', etapa.key)
  }
}

const configBuscaFilial = computed(() => ({
  ...filialSearch,
  titulo: 'Buscar Filial',
  filtrosFixos: {
    idEmpresa: authStore.empresaAtiva
  }
}))

const configBuscaFornecedor = computed(() => ({
  ...fornecedorSearch,
  titulo: 'Buscar Fornecedor',
  filtrosFixos: {
    ...fornecedorSearch.filtrosFixos,
    idEmpresa: authStore.empresaAtiva
  }
}))

const configBuscaTemplate = computed(() => ({
  ...templatePrecificacaoSearch,
  filtrosFixos: {
    lstStatus: ST.Ativo
  }
}))

const configBuscaProduto = computed(() => ({
  ...criarConfigBuscaProduto(),
  filtrosFixos: {
    ...criarConfigBuscaProduto().filtrosFixos,
    idEmpresa: authStore.empresaAtiva
  }
}))

// --- Lógica de Pricing (Atributos Dinâmicos) ---
const modalBuscaTemplate = ref(false)
const templateTemporario = ref(null)
const pricingFields = ref([])
const valoresPricing = reactive({})

const sincronizarJsonParaValores = () => {
  try {
    const data = item.value.atributosPrecificacaoJson ? JSON.parse(item.value.atributosPrecificacaoJson) : {}

    Object.keys(data).forEach(key => {
      if (key !== '_templateInfo') {
        valoresPricing[key] = data[key]
      }
    })

    if (data._templateInfo) {
      pricingFields.value = data._templateInfo.atributos || []
    }
  } catch (e) {
    console.error('Erro ao sincronizar JSON de pricing:', e)
  }
}

watch(valoresPricing, (novosValores) => {
  const payload = {
    ...novosValores,
    _templateInfo: {
      atributos: pricingFields.value
    }
  }
  item.value.atributosPrecificacaoJson = JSON.stringify(payload)
}, { deep: true })

const isContratoSaved = ref(false)

watch(() => item.value.idContratoCompra, (newId, oldId) => {
  isContratoSaved.value = !!(newId && newId > 0)

  if (isContratoSaved.value) {
    // Se oldId é undefined, estamos carregando o registro (Edição inicial)
    // Se oldId era 0 (ou outro valor) e mudou, estamos em uma transição após salvamento
    const isInitialLoad = oldId === undefined

    if (isInitialLoad) {
      accordionState.dadosBasicos = true
      accordionState.pricing = false // Edição: Pricing colapsado
      accordionState.produtos = false
      accordionState.condicoesPagamento = false
      accordionState.purchaseOrders = true // Edição: Purchase Orders expandido
    } else {
      // Regra de "Salvar e Continuar" (Adição)
      accordionState.pricing = true // Adição: Pricing expandido
      accordionState.produtos = true
      accordionState.condicoesPagamento = true
      accordionState.purchaseOrders = false // Adição: Purchase Orders colapsado
    }

    carregarProdutos()
    carregarCondicoesPagamento()
    carregarPurchaseOrders()
    sincronizarJsonParaValores()
  }
}, { immediate: true })

const abrirBuscaTemplate = () => {
  modalBuscaTemplate.value = true
}

const onTemplateSelecionado = (template) => {
  templateTemporario.value = template
  const temValores = Object.values(valoresPricing).some(v => v !== null && v !== '' && v !== 0)

  if (temValores) {
    if (modalConfirmacaoTrocaRef.value) {
      modalConfirmacaoTrocaRef.value.show()
    }
  } else {
    aplicarTemplate(template)
  }
}

const aplicarTemplate = (template) => {
  Object.keys(valoresPricing).forEach(key => delete valoresPricing[key])

  const atributos = template.estruturaJsonSchema ? JSON.parse(template.estruturaJsonSchema) : []
  pricingFields.value = atributos

  atributos.forEach(attr => {
    valoresPricing[attr.chave] = null
  })

  modalBuscaTemplate.value = false
}

const confirmarTrocaTemplate = (confirmed) => {
  if (confirmed && templateTemporario.value) {
    aplicarTemplate(templateTemporario.value)
  }
}

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['ContratoCompra', 'ContratoCompraProduto', 'ContratoCompraCondicaoPg', 'PurchaseOrder', 'Produto'])
    Object.assign(listas, result)

    if (!isEditing.value && listas.LstStatusContrato?.length > 0 && !item.value.lstStatusContrato) {
      item.value.lstStatusContrato = ''
    }
  } catch (e) {
    console.error('Erro ao carregar listas auxiliares via metadados:', e)
    emit('show-notification', { message: 'Erro ao carregar listas auxiliares via metadados.', type: 'error' })
  }
}

const toggleAccordion = (key) => {
  accordionState[key] = !accordionState[key]
}

const refCampoBuscaProduto = ref(null)

const iniciarNovoProduto = () => {
  if (!item.value.idParceiroNegocioFornecedor) {
    emit('show-notification', { message: 'Selecione um fornecedor antes de adicionar produtos.', type: 'warning' })
    return
  }

  console.log('[DEBUG] iniciarNovoProduto - item.value.percentualTolerancia:', item.value.percentualTolerancia)
  console.log('[DEBUG] iniciarNovoProduto - item.value completo:', JSON.stringify(item.value))

  produtoEmEdicao.value = {
    idContratoCompraProduto: 0,
    idContratoCompra: item.value.idContratoCompra,
    idProduto: null,
    _originalIdProduto: null,
    nomeProduto: '',
    sku: '',
    lstNaturezaItem: '',
    idTipoProduto: null,
    nomeTipoProduto: '',
    quantidadeUnFornecedorNegociada: null,
    lstUnidadeMedidaFornecedor: '',
    fatorConversao: 1,
    quantidadeUnBasicaNegociada: null,
    percentualTolerancia: item.value.percentualTolerancia,
    hasSupplierLink: false,
    lstUnidadeMedidaBasica: '',
    permiteConversaoVariavel: false
  }

  console.log('[DEBUG] produtoEmEdicao criado:', JSON.stringify(produtoEmEdicao.value))

  nextTick(() => {
    if (refCampoBuscaProduto.value) {
      refCampoBuscaProduto.value.abrirModalDeBusca()
    }
  })
}

const onProdutoSelecionado = async (p) => {
  if (!produtoEmEdicao.value) return

  produtoEmEdicao.value.nomeProduto = p.nome
  produtoEmEdicao.value.sku = p.sku
  produtoEmEdicao.value.lstNaturezaItem = p.lstNaturezaItem || ''
  produtoEmEdicao.value.idTipoProduto = p.idTipoProduto || null
  produtoEmEdicao.value.nomeTipoProduto = p.nomeTipoProduto || ''
  produtoEmEdicao.value.permiteConversaoVariavel = p.permiteConversaoVariavel || false

  // Evita que o trigger onMounted do CampoBusca limpe os dados ao abrir o modal para edição
  if (produtoEmEdicao.value._originalIdProduto === p.idProduto) {
    produtoEmEdicao.value._originalIdProduto = null
    return
  }

  produtoEmEdicao.value.quantidadeUnFornecedorNegociada = null
  produtoEmEdicao.value.quantidadeUnBasicaNegociada = null

  try {
    const res = await api.get('/ProdutoFornecedores', {
      params: {
        idProduto: p.idProduto,
        idFornecedor: item.value.idParceiroNegocioFornecedor,
        pageSize: 1000
      }
    })

    if (res.data && res.data.length > 0) {
      const link = res.data[0]
      produtoEmEdicao.value.hasSupplierLink = true
      produtoEmEdicao.value.lstUnidadeMedidaFornecedor = link.lstUnidadeFornecedor
      produtoEmEdicao.value.fatorConversao = link.fatorConversao || 1
      produtoEmEdicao.value.lstUnidadeMedidaBasica = link.lstUnidadeMedidaBaseProduto

      onQtdFornecedorUpdate(produtoEmEdicao.value.quantidadeUnFornecedorNegociada)
    } else {
      produtoEmEdicao.value.hasSupplierLink = false
      emit('show-notification', { message: 'Produto não vinculado ao fornecedor selecionado. Adicione o vínculo no Cadastro de Produtos.', type: 'error' })
    }
  } catch (error) {
    produtoEmEdicao.value.hasSupplierLink = false
    emit('show-notification', { message: 'Erro ao verificar vínculo com o fornecedor.', type: 'error' })
  }
}

const onCancelarBuscaProduto = () => {
  if (produtoEmEdicao.value && !produtoEmEdicao.value.idProduto && !produtoEmEdicao.value.idContratoCompraProduto) {
    produtoEmEdicao.value = null
  }
}

const parseNumber = (val) => Number(String(val).replace(',', '.')) || 0

const onQtdFornecedorUpdate = (val) => {
  if (!produtoEmEdicao.value) return
  produtoEmEdicao.value.quantidadeUnFornecedorNegociada = val

  if (val === null || val === '') {
    produtoEmEdicao.value.quantidadeUnBasicaNegociada = null
    return
  }

  const parsedVal = parseNumber(val)

  if (produtoEmEdicao.value.permiteConversaoVariavel) {
    return
  }

  const fator = parseNumber(produtoEmEdicao.value.fatorConversao) || 1
  produtoEmEdicao.value.quantidadeUnBasicaNegociada = parseFloat((parsedVal * fator).toFixed(4))
}

const onQtdBasicaUpdate = (val) => {
  if (!produtoEmEdicao.value) return
  produtoEmEdicao.value.quantidadeUnBasicaNegociada = val

  if (val === null || val === '') {
    produtoEmEdicao.value.quantidadeUnFornecedorNegociada = null
    return
  }

  const parsedVal = parseNumber(val)

  if (produtoEmEdicao.value.permiteConversaoVariavel) {
    return
  }

  const fator = parseNumber(produtoEmEdicao.value.fatorConversao) || 1
  produtoEmEdicao.value.quantidadeUnFornecedorNegociada = parseFloat((parsedVal / fator).toFixed(4))
}

const editarProduto = (prod) => {
  console.log('Editando produto (início):', prod)
  // Criamos uma cópia limpa e reativa
  produtoEmEdicao.value = {
    ...prod,
    hasSupplierLink: true,
    _originalIdProduto: prod.idProduto
  }

  // Buscamos informações complementares sem bloquear a abertura do modal
  api.get('/ProdutoFornecedores', {
    params: {
      idProduto: prod.idProduto,
      idFornecedor: item.value.idParceiroNegocioFornecedor,
      pageSize: 1000
    }
  }).then(res => {
    if (res.data && res.data.length > 0 && produtoEmEdicao.value) {
      produtoEmEdicao.value.lstUnidadeMedidaBasica = res.data[0].lstUnidadeMedidaBaseProduto
    }
  }).catch(e => {
    console.warn('Erro ao buscar unidade básica complementar:', e)
  })
}

const salvarProduto = async () => {
  const p = { ...produtoEmEdicao.value }
  if (!item.value.idContratoCompra) {
    emit('show-notification', { message: 'Por favor, salve o contrato antes de adicionar produtos.', type: 'warning' })
    return
  }
  if (!p.idProduto) {
    emit('show-notification', { message: 'O produto é obrigatório.', type: 'warning' })
    return
  }

  if (!p.hasSupplierLink) {
    emit('show-notification', { message: 'Produto sem vínculo com o fornecedor. O vínculo é obrigatório para todos os produtos.', type: 'error' })
    return
  }

  const payload = {
    ...p,
    idContratoCompra: item.value.idContratoCompra,
    quantidadeUnFornecedorNegociada: Number(String(p.quantidadeUnFornecedorNegociada).replace(',', '.')) || 0,
    fatorConversao: Number(String(p.fatorConversao).replace(',', '.')) || 0,
    quantidadeUnBasicaNegociada: Number(String(p.quantidadeUnBasicaNegociada).replace(',', '.')) || 0,
    percentualTolerancia: Number(String(p.percentualTolerancia).replace(',', '.')) || 0
  }

  if (p.lstUnidadeMedidaFornecedor) {
    payload.lstUnidadeMedidaFornecedor = String(p.lstUnidadeMedidaFornecedor)
  }

  try {
    if (p.idContratoCompraProduto) {
      await api.put(`/ContratoCompraProdutos/${p.idContratoCompraProduto}`, payload)
    } else {
      await api.post('/ContratoCompraProdutos', payload)
    }
    emit('show-notification', { message: 'Produto salvo com sucesso.', type: 'success' })
    produtoEmEdicao.value = null
    await carregarProdutos()
  } catch (e) {
    emit('show-notification', { message: 'Erro ao salvar produto.', type: 'error' })
  }
}

const solicitarExclusaoProduto = (prod) => {
  modalExclusaoProduto.value.id = prod.idContratoCompraProduto
  if (modalExclusaoProdutoRef.value) {
    modalExclusaoProdutoRef.value.show()
  }
}

const confirmarExclusaoProduto = async (confirmed) => {
  if (!confirmed) return
  try {
    await api.delete(`/ContratoCompraProdutos/${modalExclusaoProduto.value.id}`)
    emit('show-notification', { message: 'Produto removido com sucesso.', type: 'success' })
    await carregarProdutos()
  } catch (e) {
    emit('show-notification', { message: 'Erro ao remover produto.', type: 'error' })
  }
}

const labelInfoPrazoDias = 'Informe um valor negativo para "Antes de"'

const iniciarNovaCondicao = () => {
  if (!item.value.idContratoCompra) {
    emit('show-notification', { message: 'Por favor, salve o contrato antes de adicionar condições de pagamento.', type: 'warning' })
    return
  }

  condicaoEmEdicao.value = {
    idContratoCompraCondicaoPg: 0,
    idContratoCompra: item.value.idContratoCompra,
    percentualDoTotal: null,
    descricao: '',
    lstReferenciaTemporal: '',
    lstCondPgMomento: 'COND_PG_MOMENTO1',
    lstCondPgUnidadeTempo: 'COND_PG_UNIDADE_TEMPO1',
    lstCondPgTipoCalculo: 'COND_PG_TIPO_CALCULO1',
    prazoDias: null
  }
}

const editarCondicao = (cond) => {
  condicaoEmEdicao.value = { ...cond }
}

const salvarCondicao = async () => {
  const c = { ...condicaoEmEdicao.value }

  if (!c.lstReferenciaTemporal || c.percentualDoTotal === null) {
    emit('show-notification', { message: 'Preencha todos os campos obrigatórios da condição de pagamento.', type: 'warning' })
    return
  }

  // Gera a descrição antes de salvar
  c.descricao = gerarDescricaoCondicao(c)

  const payload = {
    ...c,
    percentualDoTotal: Number(String(c.percentualDoTotal).replace(',', '.')) || 0,
    lstCondPgMomento: c.lstCondPgMomento || 'COND_PG_MOMENTO1',
    lstCondPgUnidadeTempo: c.lstCondPgUnidadeTempo || 'COND_PG_UNIDADE_TEMPO1',
    lstCondPgTipoCalculo: c.lstCondPgTipoCalculo || 'COND_PG_TIPO_CALCULO1',
    prazoDias: Math.abs(Number(c.prazoDias) || 0)
  }

  try {
    if (c.idContratoCompraCondicaoPg) {
      await api.put(`/ContratoCompraCondicoesPg/${c.idContratoCompraCondicaoPg}`, payload)
    } else {
      await api.post('/ContratoCompraCondicoesPg', payload)
    }
    emit('show-notification', { message: 'Condição de pagamento salva com sucesso.', type: 'success' })
    condicaoEmEdicao.value = null
    await carregarCondicoesPagamento()
  } catch (e) {
    emit('show-notification', { message: 'Erro ao salvar condição de pagamento.', type: 'error' })
  }
}

const solicitarExclusaoCondicao = (cond) => {
  modalExclusaoCondicao.value.id = cond.idContratoCompraCondicaoPg
  if (modalExclusaoCondicaoRef.value) {
    modalExclusaoCondicaoRef.value.show()
  }
}

const confirmarExclusaoCondicao = async (confirmed) => {
  if (!confirmed) return
  try {
    await api.delete(`/ContratoCompraCondicoesPg/${modalExclusaoCondicao.value.id}`)
    emit('show-notification', { message: 'Condição de pagamento removida com sucesso.', type: 'success' })
    await carregarCondicoesPagamento()
  } catch (e) {
    emit('show-notification', { message: 'Erro ao remover condição de pagamento.', type: 'error' })
  }
}

const getTextoLista = (lista, id) => {
  if (!lista) return id || '-'
  const found = lista.find(l => l.idListaClienteItem === id || l.idListaSistemaItem === id)
  return found ? found.textoLista : (id || '-')
}

const gerarDescricaoCondicao = (cond) => {
  if (!cond) return ''
  const prazo = Math.abs(Number(cond.prazoDias) || 0)
  const refTemporal = getTextoLista(listas.LstReferenciaTemporal, cond.lstReferenciaTemporal)
  const percentual = cond.percentualDoTotal || 0
  const momento = getTextoLista(listas.LstCondPgMomento, cond.lstCondPgMomento)
  const unidade = getTextoLista(listas.LstCondPgUnidadeTempo, cond.lstCondPgUnidadeTempo)
  const tipoCalc = getTextoLista(listas.LstCondPgTipoCalculo, cond.lstCondPgTipoCalculo)
  return `${percentual}${tipoCalc} ${prazo} ${unidade} ${momento} ${refTemporal}`
}

const getTextoUnidadeCompleto = (id) => {
  const lista = listas.LstUnidadeMedidaFornecedor || listas.LstUnidadeMedidaBase || []
  const un = lista.find(u => u.idListaClienteItem === id)
  if (!un) return id || '-'
  return `${un.textoLista} - ${un.descricaoItem}`
}

const formatarQtd = (valor) => {
  if (valor === null || valor === undefined || valor === '') return '-'
  const num = typeof valor === 'string' ? parseNumber(valor) : Number(valor)
  if (isNaN(num)) return valor
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4
  }).format(num)
}

const formatarMoeda = (valor) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0)
}

const formatarDecimal = (valor) => {
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor || 0)
}

const getTextoUnidade = (id) => {
  const lista = listas.LstUnidadeMedidaFornecedor || listas.LstUnidadeMedidaBase || []
  const un = lista.find(u => u.idListaClienteItem === id)
  return un ? un.textoLista : (id || '-')
}

// GUARDIÃO: Camada de Validação Compartilhada (Retorna booleano)
const validarRegrasDeNegocio = async () => {
  const labelsObrigatorios = document.querySelectorAll('label.is-required')
  let hasBaseErrors = false

  // GUARDIÃO: Preserva o proxy injetado!
  // Em vez de atribuir um novo objeto ({}), deletamos e adicionamos as chaves diretamente no ref.
  for (const key in errors.value) {
    delete errors.value[key]
  }

  labelsObrigatorios.forEach(label => {
    if (label.offsetParent === null) return // Ignora inputs de modais ou abas fechadas
    const inputId = label.getAttribute('for')
    if (!inputId) return
    const inputElement = document.getElementById(inputId)
    if (!inputElement) return

    let isEmpty = false
    if (inputElement.tagName === 'SELECT') {
      isEmpty = !inputElement.value || inputElement.value === ''
    } else {
      isEmpty = !inputElement.value || String(inputElement.value).trim() === ''
    }

    if (isEmpty) {
      const nomeCampoLimpo = label.innerText.replace('*', '').trim()
      // Muta a propriedade diretamente para garantir a reatividade no CampoInput e CampoSelect
      errors.value[inputId] = `O campo "${nomeCampoLimpo}" é de preenchimento obrigatório.`
      hasBaseErrors = true
    }
  })

  if (hasBaseErrors) {
    emit('show-notification', { message: 'Por favor, preencha todos os campos obrigatórios destacados em vermelho.', type: 'warning' })
    if (focarPrimeiroErro) await focarPrimeiroErro()
    return false
  }

  // Regras de Negócio de Pricing
  if (pricingFields.value.length === 0) {
    emit('show-notification', {
      message: 'A seleção de um template de precificação é obrigatória para salvar o contrato.',
      type: 'warning'
    })
    btnTemplate.value?.focus()
    return false
  }

  for (const campo of pricingFields.value) {
    const valor = valoresPricing[campo.chave]
    if (valor === null || valor === undefined || valor === '') {
      emit('show-notification', {
        message: `Preencha o campo "${campo.label || campo.chave}" do Pricing.`,
        type: 'warning'
      })
      return false
    }
  }

  if (!item.value.valorTotalContrato || Number(item.value.valorTotalContrato) <= 0) {
    emit('show-notification', {
      message: 'O Valor Total Efetivo (Final) é obrigatório e deve ser maior que zero.',
      type: 'warning'
    })
    return false
  }

  return true
}

// GUARDIÃO: Montador do Payload Compartilhado
const montarPayload = () => {
  const payload = {
    ...item.value,
    idFilial: item.value.idFilial ? Number(item.value.idFilial) : null,
    idParceiroNegocioFornecedor: item.value.idParceiroNegocioFornecedor ? Number(item.value.idParceiroNegocioFornecedor) : null,
    valorTotalContrato: Number(item.value.valorTotalContrato) || 0
  }

  delete payload.nomeFilial
  delete payload.nomeFornecedor
  return payload
}

// GUARDIÃO: Bypass Silencioso para não fechar a tela.
const salvarEContinuar = async () => {
  if (!temContratosCompraCompleto.value || isLoading.value) return

  const isValid = await validarRegrasDeNegocio()
  if (!isValid) return

  isLoading.value = true
  const payload = montarPayload()

  try {
    const res = item.value.idContratoCompra
      ? await api.put(`/ContratosCompra/${item.value.idContratoCompra}`, payload)
      : await api.post('/ContratosCompra', payload)

    // Atualiza o item localmente SEM disparar o emit de "itemAtualizado" para o pai.
    // Como item é reativo, a watch do idContratoCompra cuidará de liberar e abrir os blocos inferiores!
    item.value = res.data

    emit('show-notification', { message: 'Dados básicos salvos com sucesso! Adicione os produtos e condições.', type: 'success' })
  } catch (error) {
    console.error('Erro ao salvar e continuar:', error)
    let msg = 'Erro ao salvar o contrato.'
    if (error.response?.data?.errors) {
      msg = Object.values(error.response.data.errors).flat()[0] || msg
    } else if (error.response?.data?.message || error.response?.data?.title) {
      msg = error.response.data.message || error.response.data.title
    } else if (typeof error.response?.data === 'string') {
      msg = error.response.data
    }
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

// GUARDIÃO: Salvamento Normal (aciona o fecho da tela ao final)
const salvar = async () => {
  if (!temContratosCompraCompleto.value || isLoading.value) return

  const isValid = await validarRegrasDeNegocio()
  if (!isValid) return

  const payload = montarPayload()

  // O submitForm padrão chamará a API, atualizará os dados locais e emitirá
  // 'itemAtualizado', sinalizando ao pai que a tela pode ser fechada.
  await submitForm('ContratosCompra', 'idContratoCompra', payload)
}

// GUARDIÃO: Salvar Alterações (persiste sem fechar a tela)
const salvarAlteracoes = async () => {
  if (!temContratosCompraCompleto.value || isLoading.value) return

  const isValid = await validarRegrasDeNegocio()
  if (!isValid) return

  const payload = montarPayload()
  try {
    const res = item.value.idContratoCompra
      ? await api.put(`/ContratosCompra/${item.value.idContratoCompra}`, payload)
      : await api.post('/ContratosCompra', payload)

    if (res?.data) {
      initializeForm(res.data)
    }
    emit('show-notification', { message: 'Alterações salvas com sucesso!', type: 'success' })
  } catch (error) {
    let msg = 'Erro ao salvar alterações.'
    if (error.response?.data?.errors) {
      msg = Object.values(error.response.data.errors).flat()[0] || msg
    } else if (error.response?.data?.message || error.response?.data?.title) {
      msg = error.response.data.message || error.response.data.title
    } else if (typeof error.response?.data === 'string') {
      msg = error.response.data
    }
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const abrirAuditoria = () => {
  mostrarAuditoria.value = true
}

const navegarParaProdutoFornecedor = () => {
  useNavegacao().abrirRegra('PRODUTOS_FORNECEDOR', 'adicionar')
}

onMounted(async () => {
  await carregarListas()
})
</script>

<template>
  <div class="contrato-compra-form-wrapper" v-bind="$attrs">
    <SemAcessoModulo v-if="!temModuloImportacao" titulo="Importação" />
    <template v-if="temModuloImportacao">
    <FormularioCadastro
      module-name="Contrato de Compra"
      :is-loading="isLoading"
      :is-editing="isEditing"
      :entity-info="entityInfoContrato"
      :titulo-registro="item.numeroContratoFornecedor"
      :ocultar-salvar="!isContratoSaved || !temContratosCompraCompleto"
      @submit="salvar"
      @submit-continuar="salvarAlteracoes"
      @cancelar="handleCancel"
      @show-notification="(p) => emit('show-notification', p)"
    >
      <!-- PIPELINE DE STATUS (PROGRESSO) -->
      <ComexPipelineStatus
        v-if="drilldownMode"
        v-bind="pipelineProps"
        @navegar="handlePipelineClick"
      />

      <template #acoes-header v-if="isEditing">
        <button
          type="button"
          class="btn-icon-minimalista me-3"
          @click="abrirAuditoria"
          title="Ver Histórico de Alterações"
        >
          <i class="bi bi-clock-history"></i>
        </button>
      </template>

      <div v-if="!podeEditar" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
        <i class="bi bi-lock-fill fs-4 me-3 text-warning"></i>
        <div><strong>Modo de Leitura:</strong> O seu perfil não tem permissões para editar este registo.</div>
      </div>

      <!-- FRAME 1: DADOS BÁSICOS -->
      <div class="card mb-3 border-primary-subtle">
        <div class="card-header bg-primary-subtle d-flex justify-content-between align-items-center cursor-pointer" @click="toggleAccordion('dadosBasicos')">
          <h5 class="mb-0 text-primary-emphasis"><i class="bi bi-file-earmark-text me-2"></i>Dados Básicos</h5>
          <i class="bi" :class="accordionState.dadosBasicos ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>
        <div v-show="accordionState.dadosBasicos" class="card-body border-top border-primary-subtle">
          <div class="row g-3">
            <div class="col-md-2">
              <CampoInput
                id="codigoContratoCompra"
                label="Código do Contrato (Interno)"
                v-model="item.codigoContratoCompra"
                disabled
                :descricao-informativa="!item.codigoContratoCompra ? 'Gerado automaticamente pelo sistema' : undefined"
              />
            </div>
            <div class="col-md-1">
              <CampoBusca
                id="idFilial"
                label="Filial"
                v-model="item.idFilial"
                :configuracao-busca="configBuscaFilial"
                required
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-3">
              <CampoBusca
                id="idParceiroNegocioFornecedor"
                label="Fornecedor"
                v-model="item.idParceiroNegocioFornecedor"
                :configuracao-busca="configBuscaFornecedor"
                required
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-2">
              <CampoInput
                id="numeroContratoFornecedor"
                label="Número do Contrato (Fornecedor)"
                v-model="item.numeroContratoFornecedor"
                required
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-1">
              <CampoInput
                id="dataContrato"
                label="Data do Contrato"
                v-model="item.dataContrato"
                type="date"
                required
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-1">
              <CampoSelect
                id="lstMoeda"
                label="Moeda"
                v-model="item.lstMoeda"
                :options="listas.LstMoeda || []"
                option-label="textoLista"
                option-value="idListaClienteItem"
                required
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-2">
              <CampoInput
                id="percentualTolerancia"
                label="Tolerância Quantitativa (%)"
                v-model="item.percentualTolerancia"
                type="decimal"
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-2">
              <CampoSelect
                id="lstStatusContrato"
                label="Status do Contrato"
                v-model="item.lstStatusContrato"
                :options="listas.LstStatusContrato || []"
                option-label="textoLista"
                option-value="idListaClienteItem"
                required
                :disabled="!podeEditar"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- FRAME 2: PRICING -->
      <div class="card mb-3 border-primary-subtle">
        <div class="card-header bg-primary-subtle d-flex justify-content-between align-items-center cursor-pointer" @click="toggleAccordion('pricing')">
          <h5 class="mb-0 text-primary-emphasis fw-bold">
            <i class="bi bi-currency-dollar me-2"></i>Pricing
            <span v-if="item.valorTotalContrato" class="ms-3 fw-normal fs-6 badge bg-primary bg-opacity-10 text-primary-emphasis border border-primary-subtle">
              Total: {{ formatarMoeda(item.valorTotalContrato) }}
            </span>
          </h5>
          <div class="d-flex align-items-center">
            <button
              v-if="temContratosCompraCompleto"
              ref="btnTemplate"
              type="button"
              class="btn btn-sm btn-primary me-3"
              @click.stop="abrirBuscaTemplate"
            >
              <i class="bi" :class="pricingFields.length > 0 ? 'bi-arrow-repeat' : 'bi-plus-lg'"></i>
              {{ pricingFields.length > 0 ? 'Trocar Template' : 'Adicionar Template' }}
            </button>
            <i class="bi" :class="accordionState.pricing ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
          </div>
        </div>
        <div v-show="accordionState.pricing" class="card-body border-top border-primary-subtle">
          <div v-if="pricingFields.length === 0" class="text-center py-4 text-muted">
            <i class="bi bi-info-circle me-2"></i>Selecione um template de precificação para habilitar os campos.
          </div>
          <div v-else class="row g-3">
            <!-- Renderização Dinâmica dos Atributos -->
            <div v-for="campo in pricingFields" :key="campo.chave" class="col-md-4">
              <CampoInput
                :id="campo.chave"
                :label="campo.label || ' '"
                v-model="valoresPricing[campo.chave]"
                required
                :disabled="!podeEditar"
              />
            </div>

            <!-- Valor Total do Contrato (Sempre ao fim, com destaque) -->
            <div class="col-12"><hr class="my-2"></div>
            <div class="col-md-4 ms-auto">
              <CampoInput
                id="valorTotalContrato"
                label="Valor Total Efetivo (Final)"
                v-model="item.valorTotalContrato"
                type="moeda"
                required
                :disabled="!podeEditar"
                class="fw-bold"
              />
              <small class="text-muted">Informe o valor total efetivo (final) do contrato.</small>
            </div>
          </div>
        </div>
      </div>

      <!-- BOTÃO DE TRANSIÇÃO (SALVAR E CONTINUAR) - Ação Direta para o método salvarEContinuar -->
      <div class="mb-4 text-center" v-if="!isContratoSaved && temContratosCompraCompleto">
        <button
          type="button"
          @click.prevent="salvarEContinuar"
          class="btn btn-success px-4 py-2 fw-bold"
          :disabled="isLoading"
        >
          <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
          <i v-if="!isLoading" class="bi bi-chevron-double-down me-1"></i>
          Salvar e Continuar
          <i v-if="!isLoading" class="bi bi-chevron-double-down ms-1"></i>
        </button>
        <div class="form-text mt-2">
          <i class="bi bi-info-circle me-1"></i> Salve os dados básicos para habilitar o cadastro de Produtos e Condições.
        </div>
      </div>

      <!-- FIELDSET CONDICIONAL PARA BLOQUEIO VISUAL ANTES DO PRIMEIRO SALVAMENTO -->
      <fieldset :disabled="!isContratoSaved" class="mb-3">
        <!-- FRAME 3: PRODUTOS DO CONTRATO -->
        <div class="card mb-3 border-primary-subtle" :class="{'frame-bloqueado': !isContratoSaved}">
          <div class="card-header bg-primary-subtle d-flex justify-content-between align-items-center" :class="{'cursor-pointer': isContratoSaved}" @click="isContratoSaved && toggleAccordion('produtos')">
            <h5 class="mb-0 text-primary-emphasis fw-bold">
              <i class="bi bi-box-seam me-2"></i>Produtos do Contrato
              <span v-if="produtosContrato.length > 0" class="ms-3 fw-normal fs-6 badge bg-primary bg-opacity-10 text-primary-emphasis border border-primary-subtle">
                {{ produtosContrato.length }} {{ produtosContrato.length === 1 ? 'produto cadastrado' : 'produtos cadastrados' }}
              </span>
              <span v-if="isContratoSaved && purchaseOrdersContrato.length > 0" class="ms-2 fw-normal fs-6 badge" :class="percentualPendentePurchaseOrders > 0 ? 'bg-warning bg-opacity-10 text-warning-emphasis border border-warning-subtle' : 'bg-success bg-opacity-10 text-success-emphasis border border-success-subtle'">
                {{ percentualPendentePurchaseOrders }}% pendente de alocação em purchase orders
              </span>
            </h5>
            <div class="d-flex align-items-center">
              <button
                v-if="temContratosCompraCompleto"
                type="button"
                class="btn btn-sm btn-primary me-3"
                @click.stop="iniciarNovoProduto"
              >
                <i class="bi bi-plus-lg"></i> Adicionar Produto
              </button>
              <i class="bi" :class="accordionState.produtos ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </div>
          </div>
          <div v-show="accordionState.produtos" class="card-body p-0 border-top border-primary-subtle">
            <div v-if="isLoadingProdutos" class="text-center py-4">
              <div class="spinner-border spinner-border-sm text-primary me-2"></div>
              Carregando produtos...
            </div>
            <div v-else class="admin-table-container border-0 shadow-none mb-0">
              <table class="table admin-table m-0">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Produto</th>
                    <th class="text-end">Qtd (Forn)</th>
                    <th>Un (Forn)</th>
                    <th class="text-end">Qtd (Base)</th>
                    <th>Un (Base)</th>
                    <th class="text-end" style="width: 100px;">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="prod in produtosContrato" :key="prod.idContratoCompraProduto">
                    <td><code>{{ prod.sku }}</code></td>
                    <td>{{ prod.nomeProduto }}</td>
                    <td class="text-end fw-bold">{{ formatarQtd(prod.quantidadeUnFornecedorNegociada) }}</td>
                    <td>{{ getTextoUnidade(prod.lstUnidadeMedidaFornecedor) }}</td>
                    <td class="text-end">{{ formatarQtd(prod.quantidadeUnBasicaNegociada) }}</td>
                    <td>{{ getTextoUnidade(prod.lstUnidadeMedidaBasica) }}</td>
                    <td class="text-end pe-3">
                      <button v-if="temContratosCompraCompleto || temVisualizacao" type="button" class="btn-icon" @click="editarProduto(prod)" :title="temContratosCompraCompleto ? 'Editar' : 'Visualizar'">
                        <i :class="temContratosCompraCompleto ? 'bi bi-pencil-fill' : 'bi bi-eye-fill text-secondary'"></i>
                      </button>
                      <button v-if="temContratosCompraCompleto" type="button" class="btn-icon text-danger" @click="solicitarExclusaoProduto(prod)" title="Excluir">
                        <i class="bi bi-trash-fill"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="produtosContrato.length === 0">
                    <td colspan="5" class="text-center py-4 text-muted">
                      <i class="bi bi-info-circle me-2"></i> Nenhum produto cadastrado neste contrato.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- FRAME 4: CONDIÇÕES DE PAGAMENTO -->
        <div class="card mb-3 border-primary-subtle" :class="{'frame-bloqueado': !isContratoSaved}">
          <div class="card-header bg-primary-subtle d-flex justify-content-between align-items-center" :class="{'cursor-pointer': isContratoSaved}" @click="isContratoSaved && toggleAccordion('condicoesPagamento')">
            <h5 class="mb-0 text-primary-emphasis fw-bold">
              <i class="bi bi-credit-card me-2"></i>Condições de Pagamento
              <span v-if="condicoesPagamentoContrato.length > 0" class="ms-3 fw-normal fs-6 badge bg-primary bg-opacity-10 text-primary-emphasis border border-primary-subtle">
                {{ condicoesPagamentoContrato.length }} {{ condicoesPagamentoContrato.length === 1 ? 'condição cadastrada' : 'condições cadastradas' }}
              </span>
            </h5>
            <div class="d-flex align-items-center">
              <button
                v-if="temContratosCompraCompleto"
                type="button"
                class="btn btn-sm btn-primary me-3"
                @click.stop="iniciarNovaCondicao"
              >
                <i class="bi bi-plus-lg"></i> Adicionar Condição
              </button>
              <i class="bi" :class="accordionState.condicoesPagamento ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </div>
          </div>
          <div v-show="accordionState.condicoesPagamento" class="card-body p-0 border-top border-primary-subtle">
            <div v-if="isLoadingCondicoes" class="text-center py-4">
              <div class="spinner-border spinner-border-sm text-info me-2"></div>
              Carregando condições...
            </div>
            <div v-else class="admin-table-container border-0 shadow-none mb-0">
              <table class="table admin-table m-0">
                <thead>
                  <tr>
                    <th>Descrição explicativa</th>
                    <th class="text-end" style="width: 100px;">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="cond in condicoesPagamentoContrato" :key="cond.idContratoCompraCondicaoPg">
                    <td>{{ gerarDescricaoCondicao(cond) }}</td>
                    <td class="text-end pe-3">
                      <button v-if="temContratosCompraCompleto || temVisualizacao" type="button" class="btn-icon" @click="editarCondicao(cond)" :title="temContratosCompraCompleto ? 'Editar' : 'Visualizar'">
                        <i :class="temContratosCompraCompleto ? 'bi bi-pencil-fill' : 'bi bi-eye-fill text-secondary'"></i>
                      </button>
                      <button v-if="temContratosCompraCompleto" type="button" class="btn-icon text-danger" @click="solicitarExclusaoCondicao(cond)" title="Excluir">
                        <i class="bi bi-trash-fill"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="condicoesPagamentoContrato.length === 0">
                    <td colspan="2" class="text-center py-4 text-muted">
                      <i class="bi bi-info-circle me-2"></i> Nenhuma condição de pagamento cadastrada.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- FRAME 5: PURCHASE ORDERS -->
        <div class="card mb-3 border-primary-subtle" :class="{'frame-bloqueado': !isContratoSaved}">
          <div class="card-header bg-primary-subtle d-flex justify-content-between align-items-center" :class="{'cursor-pointer': isContratoSaved}" @click="isContratoSaved && toggleAccordion('purchaseOrders')">
            <h5 class="mb-0 text-primary-emphasis fw-bold">
              <i class="bi bi-cart-check me-2"></i>Purchase Orders do Contrato
              <span v-if="purchaseOrdersContrato.length > 0" class="ms-3 fw-normal fs-6 badge bg-primary bg-opacity-10 text-primary-emphasis border border-primary-subtle">
                {{ purchaseOrdersContrato.length }} {{ purchaseOrdersContrato.length === 1 ? 'PO cadastrada' : 'POs cadastradas' }}
              </span>
            </h5>
            <div class="d-flex align-items-center">
              <button
                v-if="temCotasCompleto"
                type="button"
                class="btn btn-sm btn-primary me-3"
                @click.stop="abrirPurchaseOrder(null, 'adicao')"
              >
                <i class="bi bi-plus-lg"></i> Adicionar Purchase Order
              </button>
              <i class="bi" :class="accordionState.purchaseOrders ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </div>
          </div>
          <div v-show="accordionState.purchaseOrders" class="card-body p-0 border-top border-primary-subtle">
            <div v-if="isLoadingPurchaseOrders" class="text-center py-4">
              <div class="spinner-border spinner-border-sm text-info me-2"></div>
              Carregando purchase orders...
            </div>
            <div v-else class="admin-table-container border-0 shadow-none mb-0">
              <table class="table admin-table m-0">
                <thead>
                  <tr>
                    <th style="width: 80px;">ID</th>
                    <th>Nº PO</th>
                    <th>Ref.</th>
                    <th>Data Emissão</th>
                    <th>Status</th>
                    <th class="text-end">Valor Original</th>
                    <th class="text-end">Valor Negociado</th>
                    <th class="text-end" style="width: 100px;">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="po in purchaseOrdersContratoOrdenadas" :key="po.idPurchaseOrder" class="cursor-pointer" @click="temVisualizacao && abrirPurchaseOrder(po)">
                    <td><code>#{{ po.idPurchaseOrder }}</code></td>
                    <td><code>{{ po.numeroPurchaseOrder || po.nomeReferencia }}</code></td>
                    <td><code>{{ po.nomeReferencia }}</code></td>
                    <td>{{ po.dataEmissao ? new Date(po.dataEmissao).toLocaleDateString() : '-' }}</td>
                    <td>
                      <span class="badge" :class="po.lstStatusPurchaseOrder === ST.Ativo ? 'bg-warning text-dark' : 'bg-success'">
                        {{ getTextoLista(listas.LstStatusPurchaseOrder, po.lstStatusPurchaseOrder) }}
                      </span>
                    </td>
                    <td class="text-end fw-bold">{{ formatarDecimal(po.valorTotalOriginal) }}</td>
                    <td class="text-end fw-bold">{{ formatarDecimal(po.valorTotalNegociado) }}</td>
                    <td class="text-end pe-3">
                      <button type="button" class="btn-icon" title="Ver Detalhes / Editar">
                        <i class="bi bi-chevron-right"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="purchaseOrdersContrato.length === 0">
                    <td colspan="8" class="text-center py-4 text-muted">
                      <i class="bi bi-info-circle me-2"></i> Nenhuma purchase order vinculada a este contrato.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </fieldset>
    </FormularioCadastro>
    </template>

    <AuditLogViewer
      v-if="mostrarAuditoria"
      tabela="ContratoCompra"
      :chave-primaria="item.idContratoCompra"
      :titulo-registro="item.numeroContratoFornecedor"
      contexto="tenant"
      :tabelas-filhas="['ContratoCompraProduto', 'ContratoCompraCondicaoPg']"
      chave-estrangeira="IdContratoCompra"
      @fechar="mostrarAuditoria = false"
    />

    <!-- MODAIS DE PRICING -->
    <ModalDeBusca
      v-if="modalBuscaTemplate"
      :configuracao="configBuscaTemplate"
      @item-selecionado="onTemplateSelecionado"
      @fechar="modalBuscaTemplate = false"
    />

    <ConfirmationModal
      ref="modalConfirmacaoTrocaRef"
      title="Trocar Template"
      message="Ao trocar o template, os valores preenchidos no pricing atual serão perdidos. Deseja continuar?"
      @confirmed="confirmarTrocaTemplate"
    />

    <!-- MODAIS DE PRODUTO -->
    <ModalFormulario
      v-model="showModalProduto"
      :titulo="produtoEmEdicao?.idContratoCompraProduto ? 'Editar Produto do Contrato' : 'Adicionar Produto ao Contrato'"
      tamanho="lg"
      :ocultar-confirmar="!temContratosCompraCompleto"
      @confirmar="salvarProduto"
    >
      <div v-if="produtoEmEdicao" class="row g-3">
        <div class="col-md-12">
          <CampoBusca
            id="idProduto"
            label="Produto"
            v-model="produtoEmEdicao.idProduto"
            :configuracao-busca="configBuscaProduto"
            required
            ref="refCampoBuscaProduto"
            @selecionado="onProdutoSelecionado"
            @cancelar-busca="onCancelarBuscaProduto"
          />
          <div class="form-text mt-1" :class="produtoEmEdicao.permiteConversaoVariavel ? 'text-warning' : 'text-primary'">
            <i class="bi bi-info-circle me-1"></i>
            {{ produtoEmEdicao.permiteConversaoVariavel ? 'Produto com conversão variável. Digite manualmente os quantitativos' : 'A conversão das quantidades entre as unidades de medida é automática' }}
          </div>
        </div>

        <div v-if="produtoEmEdicao.idProduto && produtoEmEdicao.hasSupplierLink === false" class="col-12 mt-2">
          <div class="alert alert-danger d-flex justify-content-between align-items-center">
            <div>
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              Produto não vinculado ao fornecedor selecionado. Adicione o vínculo no Produto-Fornecedor.
            </div>
            <button type="button" class="btn btn-sm btn-outline-danger" @click="navegarParaProdutoFornecedor">
              Adicionar Relação Produto-Fornecedor
            </button>
          </div>
        </div>

        <template v-if="produtoEmEdicao.hasSupplierLink || produtoEmEdicao.idContratoCompraProduto">
          <div class="col-md-4">
            <CampoInput
              id="quantidadeUnFornecedorNegociada"
              label="Quantidade (Fornecedor)"
              :model-value="produtoEmEdicao.quantidadeUnFornecedorNegociada"
              @update:model-value="onQtdFornecedorUpdate"
              dynamic-decimals
              required
              :descricao-informativa="getTextoUnidadeCompleto(produtoEmEdicao.lstUnidadeMedidaFornecedor)"
            />
          </div>
          <div class="col-md-4">
            <CampoInput
              id="fatorConversao"
              label="Fator de Conversão"
              v-model="produtoEmEdicao.fatorConversao"
              type="decimal"
              required
              :disabled="true"
            />
          </div>

          <div class="col-md-4">
            <CampoInput
              id="quantidadeUnBasicaNegociada"
              label="Qtd. Unidade Básica"
              :model-value="produtoEmEdicao.quantidadeUnBasicaNegociada"
              @update:model-value="onQtdBasicaUpdate"
              dynamic-decimals
              required
              :descricao-informativa="getTextoUnidadeCompleto(produtoEmEdicao.lstUnidadeMedidaBasica)"
            />
          </div>

          <div class="col-12 m-0"></div> <!-- Quebra de linha explícita -->

          <div class="col-md-4">
            <CampoInput
              id="percentualTolerancia"
              label="Tolerância (%)"
              v-model="produtoEmEdicao.percentualTolerancia"
              type="decimal"
            />
          </div>

        </template>
      </div>
    </ModalFormulario>

    <ConfirmationModal
      ref="modalExclusaoProdutoRef"
      title="Excluir Produto do Contrato"
      message="Deseja realmente remover este produto do contrato?"
      @confirmed="confirmarExclusaoProduto"
    />

    <!-- MODAIS DE CONDIÇÃO DE PAGAMENTO -->
    <ModalFormulario
      v-model="showModalCondicao"
      :titulo="condicaoEmEdicao?.idContratoCompraCondicaoPg ? 'Editar Condição de Pagamento' : 'Adicionar Condição de Pagamento'"
      tamanho="lg"
      :ocultar-confirmar="!temContratosCompraCompleto"
      @confirmar="salvarCondicao"
    >
      <div v-if="condicaoEmEdicao" class="row g-3">
        <div class="col-md-3">
          <CampoInput
            id="prazoDias"
            label="Prazo em Dias"
            v-model="condicaoEmEdicao.prazoDias"
            type="number"
            step="1"
            min="0"
            required
            :label-info="labelInfoPrazoDias"
          />
        </div>
        <div class="col-md-3">
          <CampoSelect
            id="lstCondPgMomento"
            label="Momento"
            v-model="condicaoEmEdicao.lstCondPgMomento"
            :options="listas.LstCondPgMomento || []"
            option-value="idListaSistemaItem"
            option-label="textoLista"
            required
          />
        </div>
        <div class="col-md-6">
          <CampoBuscaLista
            id="lstReferenciaTemporal"
            label="Referência Temporal"
            v-model="condicaoEmEdicao.lstReferenciaTemporal"
            tabela="ContratoCompraCondicaoPg"
            required
          />
        </div>
        <div class="col-md-3">
          <CampoInput
            id="percentualDoTotal"
            label="% do Total"
            v-model="condicaoEmEdicao.percentualDoTotal"
            type="moeda"
            required
          />
        </div>
        <div class="col-12 mt-3">
          <div class="alert alert-info py-2 mb-0">
            <i class="bi bi-info-circle me-2"></i>
            <strong>Descrição:</strong> {{ gerarDescricaoCondicao(condicaoEmEdicao) }}
          </div>
        </div>
      </div>
    </ModalFormulario>

    <ConfirmationModal
      ref="modalExclusaoCondicaoRef"
      title="Excluir Condição de Pagamento"
      message="Deseja realmente remover esta condição de pagamento?"
      @confirmed="confirmarExclusaoCondicao"
    />
  </div>
</template>

<style scoped>
.frame-bloqueado {
  opacity: 0.6;
  filter: grayscale(0.5);
  transition: all 0.3s ease;
}

.cursor-pointer {
  cursor: pointer;
}

.admin-table-container {
  background-color: transparent;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.25rem;
  overflow-x: auto;
}
.admin-table thead th {
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-heading-color);
  font-weight: 600;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--bs-border-color);
}
.admin-table td {
  border-bottom: 1px solid var(--bs-border-color);
  color: var(--bs-body-color);
  padding: 0.5rem 0.75rem;
}
.btn-icon { background: none; border: none; color: #6c7d8e; padding: 0.4rem; transition: 0.2s; }
.btn-icon:hover { color: #212934; }
[data-bs-theme="dark"] .btn-icon:hover { color: #fff; }
</style>
